import React from 'react'

interface TokenInfo {
  name?: string
  symbol?: string
  standard?: string
}

interface ContractAddress {
  address: string
  network: string
  explorerUrl?: string
}

interface BlockchainDetailsProps {
  contractAddresses?: ContractAddress[]
  tokenInfo?: TokenInfo
}

// Network display names mapping
const networkDisplayNames: Record<string, string> = {
  'ethereum-mainnet': 'Ethereum Mainnet',
  'ethereum-goerli': 'Ethereum Goerli',
  'ethereum-sepolia': 'Ethereum Sepolia',
  polygon: 'Polygon',
  'polygon-mumbai': 'Polygon Mumbai',
  bsc: 'Binance Smart Chain',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
  avalanche: 'Avalanche',
  solana: 'Solana',
  other: 'Other Network',
}

// Token standard display names
const standardDisplayNames: Record<string, string> = {
  erc20: 'ERC-20',
  erc721: 'ERC-721',
  erc1155: 'ERC-1155',
  bep20: 'BEP-20',
  spl: 'SPL Token',
  other: 'Other',
}

const BlockchainDetails: React.FC<BlockchainDetailsProps> = ({ contractAddresses, tokenInfo }) => {
  if (!contractAddresses?.length && !tokenInfo) {
    return null
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex items-center mb-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 mr-2 text-primary"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
            clipRule="evenodd"
          />
        </svg>
        <h3 className="text-lg font-semibold">Blockchain Details</h3>
      </div>

      {/* Contract Addresses */}
      {contractAddresses && contractAddresses.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-medium mb-3 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Smart Contracts
          </h4>
          <div className="space-y-3">
            {contractAddresses.map((contract, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-primary">
                    {networkDisplayNames[contract.network] || contract.network}
                  </span>
                  {contract.explorerUrl && (
                    <a
                      href={contract.explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:text-primary-dark transition-colors inline-flex items-center"
                    >
                      View on Explorer
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 ml-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </a>
                  )}
                </div>
                <div className="text-xs font-mono bg-white dark:bg-gray-800 p-2 rounded border break-all">
                  {contract.address}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Token Info */}
      {tokenInfo && (tokenInfo.name || tokenInfo.symbol || tokenInfo.standard) && (
        <div>
          <h4 className="text-md font-medium mb-3 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
            Token Information
          </h4>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md border border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-1 gap-3">
              {tokenInfo.name && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Name:
                  </span>
                  <span className="text-sm font-semibold">{tokenInfo.name}</span>
                </div>
              )}
              {tokenInfo.symbol && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Symbol:
                  </span>
                  <span className="text-sm font-mono font-semibold bg-primary/10 text-primary px-2 py-1 rounded">
                    {tokenInfo.symbol}
                  </span>
                </div>
              )}
              {tokenInfo.standard && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Standard:
                  </span>
                  <span className="text-sm font-mono font-semibold">
                    {standardDisplayNames[tokenInfo.standard] || tokenInfo.standard}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BlockchainDetails
